import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config';

interface VoiceConfig {
  voice_type?: string;
  voice_speed?: number;
  voice_model?: string;
  auto_voice_response?: boolean;
}

interface VoiceChatInterfaceProps {
  onSendMessage?: (message: string) => void;
  onTranscription?: (message: string) => void;
  onTextToSpeech?: (text: string) => Promise<void> | void;
  voiceConfig?: VoiceConfig | null;
  isLoading?: boolean;
  disabled?: boolean;
  voiceEnabled?: boolean;
  autoVoiceResponse?: boolean;
  voiceType?: string;
  voiceSpeed?: number;
  voiceModel?: string;
}

const VoiceChatInterface: React.FC<VoiceChatInterfaceProps> = ({
  onSendMessage,
  onTranscription,
  onTextToSpeech,
  voiceConfig,
  isLoading = false,
  disabled = false,
  voiceEnabled,
  autoVoiceResponse,
  voiceType,
  voiceSpeed,
  voiceModel,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [transcription, setTranscription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const effectiveIsLoading = isLoading || disabled;
  const effectiveVoiceEnabled = voiceEnabled ?? Boolean(voiceConfig);

  // Inicializar gravação de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        transcribeAudio(audioBlob);
        
        // Parar todas as tracks do stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setError(null);
    } catch (err) {
      setError('Erro ao acessar o microfone. Verifique as permissões.');
      console.error('Erro ao iniciar gravação:', err);
    }
  };

  // Parar gravação
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Transcrever áudio usando Speech-to-Text
  const transcribeAudio = async (audioBlob: Blob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.wav');
      
      const response = await axios.post(`${API_URL}/speech-to-text`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const transcribedText = response.data.transcription;
      setTranscription(transcribedText);
      
      if (transcribedText.trim()) {
        onTranscription?.(transcribedText);
        if (!onTranscription && onSendMessage) {
          onSendMessage(transcribedText);
        }
      }
    } catch (err) {
      setError('Erro ao transcrever áudio. Tente novamente.');
      console.error('Erro na transcrição:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Parar reprodução de áudio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Limpar recursos ao desmontar componente
  useEffect(() => {
    const audioEl = audioRef.current;
    return () => {
      if (audioEl) {
        audioEl.pause();
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  if (!effectiveVoiceEnabled) {
    return null;
  }

  return (
    <Box>
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 2, 
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          🎤 Controles de Voz
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Botão de Gravação */}
          <Tooltip title={isRecording ? 'Parar gravação' : 'Iniciar gravação'}>
            <IconButton
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing || effectiveIsLoading}
              color={isRecording ? 'error' : 'primary'}
              size="large"
              sx={{
                backgroundColor: isRecording ? 'error.light' : 'primary.light',
                '&:hover': {
                  backgroundColor: isRecording ? 'error.main' : 'primary.main',
                },
                color: 'white'
              }}
            >
              {isRecording ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          </Tooltip>

          {/* Botão de Reprodução/Parar Áudio */}
          <Tooltip title={isPlaying ? 'Parar áudio' : 'Reproduzir último áudio'}>
            <IconButton
              onClick={isPlaying ? stopAudio : () => {}}
              disabled={!audioBlob && !isPlaying}
              color={isPlaying ? 'warning' : 'default'}
              size="large"
            >
              {isPlaying ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </Tooltip>

          {/* Indicador de Processamento */}
          {(isProcessing || effectiveIsLoading) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {isRecording ? 'Gravando...' : isProcessing ? 'Processando...' : 'Enviando...'}
              </Typography>
            </Box>
          )}

          {/* Transcrição */}
          {transcription && (
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="body2" color="text.secondary">
                Transcrição: "{transcription}"
              </Typography>
            </Box>
          )}
        </Box>

        {/* Configurações de Voz Ativas */}
        <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Voz: {voiceType} | Velocidade: {voiceSpeed}x | Modelo: {voiceModel}
          </Typography>
          {autoVoiceResponse && (
            <Typography variant="caption" color="success.main">
              ✓ Resposta automática em voz ativada
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Snackbar para Erros */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Elemento de áudio oculto */}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </Box>
  );
};

export default VoiceChatInterface;
export type { VoiceChatInterfaceProps };
